/**
 * Custom hook for managing onboarding form state and validation
 */
import { useState, ChangeEvent } from 'react';
import { OnboardingFormData, TouchedFields } from '../types';
import { initialFormData, initialTouchedState } from '../constants';
import { nameValidation, descriptionValidation } from '../validation';

/**
 * Hook for managing onboarding form state
 * @returns Form state, handlers, and validation helpers
 */
export const useOnboardingForm = () => {
  const [formData, setFormData] = useState<OnboardingFormData>(initialFormData);
  const [touchedFields, setTouchedFields] =
    useState<TouchedFields>(initialTouchedState);
  const [nameUniquenessError, setNameUniquenessError] = useState<
    string | undefined
  >();
  const [isCheckingName, setIsCheckingName] = useState(false);

  /**
   * Handles form field changes
   * @param event - The change event from input field
   */
  const onFormChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fieldName = event?.target?.name;
    const newValue = event?.target?.value;

    setFormData(prev => ({
      ...prev,
      [fieldName]: newValue,
    }));

    // Reset touched state if field is cleared
    if (newValue.length === 0) {
      setTouchedFields(prev => ({
        ...prev,
        [fieldName]: false,
      }));
    }
  };

  /**
   * Marks a field as touched for validation display
   * @param fieldName - The field to mark as touched
   */
  const handleFieldBlur = (fieldName: keyof TouchedFields) => {
    setTouchedFields(prev => ({
      ...prev,
      [fieldName]: true,
    }));
  };

  /**
   * Checks if the form is valid for current step
   * @returns True if all required fields are valid
   */
  const isFormValid = (): boolean => {
    return (
      formData.name.length > 0 &&
      !nameValidation(formData.name) &&
      !descriptionValidation(formData.description) &&
      !nameUniquenessError &&
      !isCheckingName
    );
  };

  /**
   * Marks all fields as touched to show validation errors
   */
  const touchAllFields = () => {
    setTouchedFields({
      name: true,
      description: true,
      identifier: true,
    });
  };

  /**
   * Resets the form to initial state
   */
  const resetForm = () => {
    setFormData(initialFormData);
    setTouchedFields(initialTouchedState);
    setNameUniquenessError(undefined);
    setIsCheckingName(false);
  };

  /**
   * Gets trimmed form data for submission
   * @returns Form data with trimmed values
   */
  const getTrimmedFormData = () => ({
    name: formData.name.trim(),
    description: formData.description.trim(),
  });

  return {
    formData,
    touchedFields,
    onFormChange,
    handleFieldBlur,
    isFormValid,
    touchAllFields,
    resetForm,
    getTrimmedFormData,
    nameUniquenessError,
    setNameUniquenessError,
    isCheckingName,
    setIsCheckingName,
  };
};
